import { Box, Heading, Paragraph } from '@zillow/constellation-10';

import type { Lease, LeaseAction, Property } from '../../../models';
import LeaseActionButtonGroup from '../../../components/LeaseActions/LeaseActionButtonGroup';
import type { SummaryTextValue } from '../../../components/LeaseSummary/SummaryTitle';
import { SummaryText, summaryTextMap } from '../../../components/LeaseSummary/SummaryTitle';

type Props = {
  summaryText: string;
  lease: Lease;
  property: Property;
  summaryActions: Array<LeaseAction>;
  showDescription: boolean;
  propertyAddress: string;
  leaseStartDate: string;
  disableCta: boolean;
  hasColandlord?: boolean;
};

const headerTextMap = {
  [SummaryText.COMPLETE_EACH_SECTION]: () => ({
    title: 'Complete each section',
    description:
      'Your lease can be ready for signing soon. Once these sections are complete, you just need to show everyone where to sign.',
  }),
  [SummaryText.NEARLY_READY_TO_BE_SIGNED]: () => ({
    title: 'Your lease is nearly ready to be signed',
    description: 'To finish getting your lease ready, you need to mark where everyone should sign it',
  }),
  [SummaryText.SIGNATURES_IN_PROGRESS]: () => ({
    title: 'Signatures are in progress',
    description:
      'Your lease has been shared with your renters to be signed online. Once they sign the lease, you’ll be able to countersign it. In the meantime, you can view your prepared lease or cancel the signing.',
  }),
  [SummaryText.COLANDLORD_SIGNED]: () => ({
    title: 'All your tenants and the co-landlord have signed. It’s your turn.',
    description: 'It’s your turn to sign and finalize the lease',
  }),
  [SummaryText.SIGNING_CANCELLED]: () => ({
    title: 'Your lease was canceled',
    description:
      'If you still want to move forward with the lease, you can make any other needed changes here, then prepare the lease, and then request signatures again',
  }),
  [SummaryText.EDIT_SENT_FOR_SIGNATURES]: () => ({
    title: 'Editing a lease that has been sent for signatures',
    description:
      'You are editing a lease agreement that has already been shared with your renters. They can still access the old lease, and they know you are updating it. However, your renters cannot view the changes until you request signatures again. This will restart the signing process and share out your updated lease.',
  }),
  [SummaryText.SIGNING_COMPLETE]: () => ({
    title: 'Congratulations, your lease is signed',
    description:
      'All parties have signed. You can no longer make changes to this lease agreement, but you can view and download the signed lease here.',
  }),
  [SummaryText.LEASE_ACTIVE]: () => ({
    title: 'Your lease is active',
    description:
      'All parties have signed. You can no longer make changes to this lease agreement, but you can view and download the signed lease here.',
  }),
  [SummaryText.LEASE_EXPIRED]: () => ({
    title: 'This lease has expired',
    description: 'View your expired lease here',
  }),
  [SummaryText.LEASE_TERMINATED]: () => ({
    title: 'This lease has been canceled',
    description: 'View your canceled lease here',
  }),
  [SummaryText.TENANTS_HAVE_SIGNED]: (hasColandlord) => ({
    title: hasColandlord
      ? 'All your tenants and the co-landlord have signed. It’s your turn.'
      : 'All tenants have signed. It’s your turn.',
    description:
      'It’s your turn to sign and finalize the lease. You can review your list of terms ' +
      'or view the full lease to sign it.',
  }),
};

export default function SelfServeActionContainer({
  disableCta,
  summaryText,
  propertyAddress,
  leaseStartDate,
  hasColandlord,
  lease,
  property,
  summaryActions,
  showDescription,
}: Props) {
  let headerTextValue: SummaryTextValue;
  if (headerTextMap[summaryText]) {
    headerTextValue = headerTextMap[summaryText](hasColandlord);
  } else if (summaryTextMap[summaryText]) {
    headerTextValue = summaryTextMap[summaryText](propertyAddress, null, leaseStartDate, hasColandlord);
  } else {
    headerTextValue = { title: '', description: '' };
  }

  return (
    <Box
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 'tight',
      }}
    >
      {showDescription ? (
        <Box>
          <Heading
            level={2}
            css={{
              textStyle:
                'responsive.heading-sm' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tighter',
            }}
          >
            {headerTextValue.title}
          </Heading>
          <Paragraph>{headerTextValue.description}</Paragraph>
        </Box>
      ) : (
        <Heading
          level={2}
          css={{
            textStyle:
              'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
          }}
        >
          {headerTextValue.title}
        </Heading>
      )}
      <LeaseActionButtonGroup
        {...{ property, lease }}
        isTop={showDescription}
        leaseActions={summaryActions}
        disabled={disableCta}
      />
    </Box>
  );
}
